/**
 * Shared rules for "which status moves may this leg make, right now?".
 *
 * Two surfaces read this: the driver app's Ready to Depart / I've Arrived
 * buttons (`AssignmentCard`) and dispatch's Update Leg Status modal
 * (`LegStatusDialog`). Keeping the verdict in one pure function is what keeps
 * the two from drifting the way the confirm gates used to.
 *
 * Rules (agreed 2026-09-11):
 *  - Forward only. `booked → confirmed → in_transit → delivered`; a delivered
 *    or canceled leg is read-only and nothing ever moves backwards.
 *  - `delivered` requires the leg to be `in_transit` first (mirrors
 *    `progressLeg`'s `requirePriorInTransit` guard).
 *  - Dispatchers may move Manual legs only; admins may move any trip type.
 *    Drivers only ever see Manual legs (non-manual departure is automated).
 *  - A trailer flagged needs_service blocks departure, not arrival.
 *
 * This module is pure — no Supabase, no React. The write path stays
 * `progressLeg` in `@/lib/leg-progression`.
 */
import type { OrderStatus } from "@/types/tms";
import { resolveLegStatus } from "@/lib/leg-status-resolve";

export type LegStatusMove = "in_transit" | "delivered" | "canceled";

export interface LegStatusRow {
  id: string;
  leg_sequence?: number | null;
  driver_status?: OrderStatus | string | null;
  /** Active EAPI trip status for this leg, when one exists. */
  eapi_status?: string | null;
  trip_type?: string | null;
}

export interface LegStatusActionsInput {
  legs: LegStatusRow[];
  /** Parent order status; used as the fallback when a leg has no own status. */
  orderStatus?: OrderStatus | string | null;
  /** Order-level trip type, used when a leg row carries none. */
  orderTripType?: string | null;
  isAdmin?: boolean;
  /** The driver app passes true; it never offers non-manual departures. */
  isDriver?: boolean;
  /** Blocks departure only. */
  trailerNeedsService?: boolean;
}

export interface LegStatusAction {
  move: LegStatusMove;
  label: string;
  allowed: boolean;
  /** Plain-language reason, present exactly when `allowed` is false. */
  reason: string | null;
}

export interface LegStatusPlanRow {
  legId: string;
  legSequence: number | null;
  /** Effective status of the leg (own status, else the order's). */
  status: string | null;
  tripType: string | null;
  /** True once the leg can no longer move at all. */
  locked: boolean;
  actions: LegStatusAction[];
}

export interface LegStatusPlan {
  legs: LegStatusPlanRow[];
  /** True when at least one leg has at least one allowed move. */
  canMoveAny: boolean;
}

const MOVE_LABEL: Record<LegStatusMove, string> = {
  in_transit: "Mark In Transit",
  delivered: "Mark Delivered",
  canceled: "Cancel Leg",
};

export function planLegStatusActions(input: LegStatusActionsInput): LegStatusPlan {
  const {
    legs,
    orderStatus = null,
    orderTripType = null,
    isAdmin = false,
    isDriver = false,
    trailerNeedsService = false,
  } = input;

  const isMultiLeg = legs.length > 1;

  const rows: LegStatusPlanRow[] = legs.map((leg) => {
    // Same resolver the leg tabs on the order page use, so a leg never reads
    // one status in the tab and another in this modal.
    const status = (resolveLegStatus(leg, orderStatus, isMultiLeg) ?? null) as string | null;
    const tripType = (leg.trip_type ?? orderTripType ?? null) as string | null;
    const isManual = tripType === "Manual";
    // Non-admin dispatchers and every driver are limited to Manual legs:
    // an autonomous trip's status comes back from Aurora's dispatch system,
    // and stamping it by hand would immediately be contradicted by the poller.
    const tripTypeBlocked = !isManual && !(isAdmin && !isDriver);
    const locked =
      status === "delivered" || status === "canceled" || status === "tender_received";

    const reasonFor = (move: LegStatusMove): string | null => {
      if (status === "canceled") return "This leg is cancelled.";
      if (status === "delivered") return "This leg is already delivered.";
      if (status === "tender_received") return "This leg hasn't been booked yet.";
      // Cancelling is allowed for admins AND dispatchers on any trip type:
      // unlike a hand-stamped departure, it also retires the upstream trip,
      // so the poller cannot contradict it.
      if (move === "canceled") {
        if (isDriver) return "Only dispatch can cancel a stop.";
        return null;
      }
      if (tripTypeBlocked) {
        return isDriver
          ? "This trip runs autonomously, so its status updates on its own."
          : "Only an admin can change the status of a non-manual leg.";
      }
      if (move === "in_transit") {
        if (status === "in_transit") return "This leg is already in transit.";
        if (status !== "confirmed") {
          return "This leg hasn't been confirmed by dispatch yet.";
        }
        if (trailerNeedsService) {
          return "The trailer is marked as needing service.";
        }
        return null;
      }
      // delivered
      if (status !== "in_transit") return "Mark the leg in transit first.";
      return null;
    };

    const moves: LegStatusMove[] = isDriver
      ? ["in_transit", "delivered"]
      : ["in_transit", "delivered", "canceled"];
    const actions: LegStatusAction[] = moves.map(
      (move) => {
        const reason = reasonFor(move);
        return { move, label: MOVE_LABEL[move], allowed: reason === null, reason };
      },
    );

    return {
      legId: leg.id,
      legSequence: leg.leg_sequence ?? null,
      status,
      tripType,
      locked,
      actions,
    };
  });

  return {
    legs: rows,
    canMoveAny: rows.some((r) => r.actions.some((a) => a.allowed)),
  };
}

/** Convenience for a single leg — the driver app's shape. */
export function planSingleLegStatusActions(
  leg: LegStatusRow,
  rest: Omit<LegStatusActionsInput, "legs">,
): LegStatusPlanRow {
  return planLegStatusActions({ ...rest, legs: [leg] }).legs[0];
}
